<template>
    <card xs-tile sm-tile class="flex-grow-1">
        <card-text fill class="p-0">
            <div v-if="pageStyle && pageStyle.content" class="p-16 border-b">
                <user-content :text="pageStyle.content" />
            </div>

            <div class="flex items-center border-b p-16">
                <form-input name="search"
                            v-model="filterInputData.search"
                            hide-details
                            inverted
                            clearable
                            class="lg:min-w-500"
                            :icon="icons.search"
                            :placeholder="$t('placeholder.search')" @update:modelValue="handleSearchInput" @clear="handleSearchInput"/>

                <form-date-input v-model="filterInputData.min_date"
                                      class="ml-8 none sm:block"
                                      name="min_date"
                                      time
                                      hide-details
                                      inverted
                                      clearable
                                      :color="eventStore.colorStyle"
                                      :rules="{max: filterInputData.max_date}"
                                      :label="$t('label.min_date')" @blur="applyFilterInputs" @clear="applyFilterInputs"/>

                <form-date-input v-model="filterInputData.max_date"
                                      class="ml-8 none sm:block"
                                      name="max_date"
                                      time
                                      hide-details
                                      inverted
                                      clearable
                                      :color="eventStore.colorStyle"
                                      :rules="{min: filterInputData.min_date}"
                                      :label="$t('label.max_date')" @blur="applyFilterInputs" @clear="applyFilterInputs"/>

                <div class="flex">
                    <btn :color="filterExists ? eventStore.colorStyle : null" @click.stop="openFilterModal" class="sm:none ml-8">
                        <icon :icon="icons.extended"/>
                    </btn>
                    <btn v-if="filterExists" @click.prevent="resetFilter" class="ml-8">
                        <icon :icon="icons.reset"/>
                    </btn>
                </div>
            </div>

            <fetch-loader v-if="pending" :pending="pending" @retry="refresh" fill/>
            <template v-else>

                <template v-if="jobs.length">
                    <job-list-item v-for="job in jobs" :key="`job-${job.id}`" :job="job" :to="$web('events.jobs.show', [eventStore.event._key, job._key])"/>
                </template>

                <div class="px-16">
                    <expansion-panels v-if="categories.length" v-model="openCategory" accordion :class="{'border-t': jobs.length}">
                        <expansion-panel v-for="(category, categoryIndex) in categories" :key="`category-${category.id}`" :value="category.slug" medium :title="category.name" :subtitle="category.description"
                                         @update:modelValue="open => handleToggledCategory(categoryIndex, open)" class="py-8">
							<loader v-if="!Array.isArray(category.jobs)" :value="category.jobs" class="px-0 py-0">
								<template #loading>
									<div class="flex flex-column w-full">
										<job-list-item-skeleton v-for="i in 10" :key="i" class="px-8" without-thumbnail without-creator/>
									</div>
								</template>
							</loader>

                            <template v-else>
                                <job-list-item v-for="job in category.jobs" :key="`job-${job.id}`" :job="job" :to="$web('events.jobs.show', [eventStore.event._key, job._key])"/>
                            </template>
                        </expansion-panel>
                    </expansion-panels>
                </div>

                <missing-info v-if="!jobs.length && !categories.length" :text="filterExists ? $t('missing.search') : $t('missing.job.index')" class="flex-grow-1" no-spacing/>
            </template>
        </card-text>

        <client-only>
			<modal v-if="$breakpoint.xsOnly" v-model="showFilterModal" :title="$t('action.search_and_filter')">
				<form-input v-model="filterInputData.search"
							type="search"
							enterkeyhint="next"
							:label="$t('action.search')" clearable/>

				<form-date-input v-model="filterInputData.min_date"
									  name="min_date"
									  clearable
									  time
									  :color="eventStore.colorStyle"
									  :rules="{max: filterInputData.max_date}"
									  :label="$t('label.min_date')"/>

				<form-date-input v-model="filterInputData.max_date"
									  name="max_date"
									  clearable
									  time
									  :color="eventStore.colorStyle"
									  :rules="{min: filterInputData.min_date}"
									  :label="$t('label.max_date')"/>

				<template #button>
					<btn @click.prevent="applyFilterInputs" color="success">
						<icon :icon="icons.submit"/>
						<span>{{ $t('action.apply_filter') }}</span>
					</btn>
				</template>
			</modal>
        </client-only>

    </card>
</template>

<script setup lang="ts">
import {EventPageType} from "@spoferan/spoferan-ts-core";
import {scrollToSelector} from "@spoferan/spoferan-ts-core";
import {mdiTune} from "@mdi/js";
import {deepEqual} from "@spoferan/spoferan-ts-core";
import {debounce} from "@spoferan/spoferan-ts-core";
import {submitIcon, searchIcon, resetIcon} from "@spoferan/nuxt-spoferan/icons";
import {useEventStore} from "../../../../store/event";

const eventStore = useEventStore();
const {t} = useI18n();
const {$api, $web, $apiFetch, $date, $breakpoint} = useNuxtApp();
const route = useRoute();
const router = useRouter();

// Make sure the page is activated
const page = eventStore.getPage(EventPageType.Jobs)
if (!page) {
	await navigateTo($web('events.show', route.params.event));
}

const pageStyle = ref({});
const categories = ref([]);
const jobs = ref([]);
const openCategory = ref(null);
const filterData = ref({});
const filterInputData = ref({});
const showFilterModal = ref(false);

const icons = {
	submit: submitIcon,
	extended: mdiTune,
	search: searchIcon,
	reset: resetIcon,
};

const filterExists = computed(() => {
	return Object.values(filterData.value).filter(value => !!value).length > 0
});

const {data: responses, pending, refresh} = await useAsyncData('jobs-index', loadData);

categories.value = responses.value[0].data;
jobs.value = responses.value[1].data;
pageStyle.value = pageStyle.value ?? responses.value[2].data

watch(responses, (newResponses) => {
	categories.value = newResponses ? newResponses[0].data : [];
	jobs.value = newResponses ? newResponses[1].data : [];
});

onMounted(() => {
	if (route.hash) {
		const hash = route.hash.substr(1);
		const categoryIndex = categories.value.findIndex(category => category.slug === hash);
		if (categoryIndex > -1 && !openCategory.value) {
			openCategory.value = route.hash.substr(1);
			handleToggledCategory(categoryIndex, true);
		}
	}
});

async function loadData() {
	const jobCategoriesFilter = {
		with_postings: true
	};
	Object.keys(filterData.value).forEach(key => {
		jobCategoriesFilter[`job_${key}`] = filterData.value[key];
	});

	const requests = [
		$apiFetch($api('events.position_categories.index', eventStore.event.id), {
			guest: true,
			params: jobCategoriesFilter,
		}),
		loadJobsForCategoryId(0)
	];

	// Prevent loading the page style on filters
	if (pageStyle.value === null && page) {
		requests.push($apiFetch($api('events.pages.show', [eventStore.event.id, page.id]), {
			guest: true,
			params: {
				ts: page.cache_buster,
			}
		}));
	}

	return await Promise.all(requests);
}

function handleToggledCategory(categoryIndex, open) {
	if (open) {
		if (!categories.value[categoryIndex].jobs) {
			categories.value[categoryIndex]['jobs'] = true; // Set loading state
			loadJobsForCategoryId(categories.value[categoryIndex].id).then(response => {
				categories.value[categoryIndex]['jobs'] = response.data;
				setCategoryAnchorAndFocus(categories.value[categoryIndex]);
			}).catch((error) => {
				categories.value[categoryIndex]['jobs'] = error; // Set error state
			})
		} else {
			setCategoryAnchorAndFocus(categories.value[categoryIndex]);
		}
	}
}

function loadJobsForCategoryId(id) {
	return $apiFetch($api('events.job_postings.index', eventStore.event.id), {
		params: {
			...filterData.value,
			category_id: id
		}
	})
}

function resetFilter() {
	filterInputData.value = {};
	applyFilterInputs();
}

function openFilterModal() {
	showFilterModal.value = true
}

function applyFilterInputs() {
	showFilterModal.value = false;

	if (!deepEqual(filterData.value, filterInputData.value)) {
		openCategory.value = null;
		filterData.value = {...filterInputData.value};
		if (!pending.value) {
			refresh();
		}
	}
}

const handleSearchInput = debounce(function () {
	if (!pending.value) {
		applyFilterInputs();
	}
}, 300)

	function setCategoryAnchorAndFocus(category) {
	const categoryValue = category ? category.slug : null;
	if (categoryValue) {
		setTimeout(() => {
			if (route.hash !== `#${categoryValue}`) {
				router.replace({hash: `#${categoryValue}`});
			} else {
				// -56 for app header
				// mobile: -42 for sticky header toolbar
				scrollToSelector(route.hash, -56 - ($breakpoint.xsOnly ? 42 : 0));
			}
		}, 150);
	} else if (route.hash) {
		router.replace({hash: null});
	}
}

useMeta({
	title: eventStore.buildPageTitle(t('view.events.jobs.index.title', {event: eventStore.event.display_name})),
	description: t('view.events.jobs.index.description', {event: eventStore.event.display_name}),
	canonical: $web('jobs.events.show', [eventStore.event._key], true)
})

</script>

